import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  const navItems = [
    { to: "/shop", label: "Shop" },
    { to: "/about", label: "About" },
  ];

  return (
    <header
      className="sticky top-0 z-40 border-b backdrop-blur-xl"
      style={{ background: "rgba(253,251,247,0.85)", borderColor: "var(--line)" }}
      data-testid="site-header"
    >
      <div
        className="max-w-7xl mx-auto px-3 sm:px-6 grid grid-cols-3 items-center"
        style={{ minHeight: "90px" }}
      >
        <div className="flex items-center">
          <button
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <Link
          to="/"
          className="justify-self-center inline-flex items-center"
          data-testid="brand-logo"
          aria-label="Tamoré by Tamanna — Home"
        >
          <img
            src="/tamore-logo.png"
            alt="Tamoré by Tamanna"
            width="788"
            height="236"
            loading="eager"
            decoding="sync"
            fetchpriority="high"
            className="block h-auto w-[132px] sm:w-[164px] lg:w-[204px] translate-y-[2px]"
            style={{
              userSelect: "none",
              imageRendering: "-webkit-optimize-contrast",
            }}
            draggable={false}
          />
        </Link>

        <div className="flex items-center justify-end gap-5">
          <Link
            to={user ? "/account" : "/login"}
            className="nav-link hidden sm:inline-flex items-center gap-2"
            data-testid="account-link"
          >
            <User size={16} strokeWidth={1.5} />
            <span className="hidden lg:inline">{user ? "Account" : "Sign In"}</span>
          </Link>
          <button
            onClick={() => setOpen(true)}
            className="relative nav-link inline-flex items-center gap-2"
            data-testid="cart-icon-button"
            aria-label="cart"
          >
            <ShoppingBag size={16} strokeWidth={1.5} />
            <span className="hidden lg:inline">Bag</span>
            {count > 0 && (
              <span
                className="absolute -top-2 -right-3 text-[10px] font-medium rounded-full w-5 h-5 flex items-center justify-center"
                style={{ background: "var(--ink)", color: "var(--bone)" }}
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t" style={{ borderColor: "var(--line)" }}>
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className="nav-link"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to={user ? "/account" : "/login"}
              onClick={() => setMobileOpen(false)}
              className="nav-link"
              data-testid="mobile-account-link"
            >
              {user ? "Account" : "Sign In"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
